import { useCallback, useState, useEffect } from "react";
import { useAuthHook } from "@/hooks/useAuth";
import { useAuth } from "../../context/AuthContext"
import { Property } from "../../data/models";
import PropertyCard from "../../components/valuation/PropertyCard";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/institutionalClient/Sidebar";
import Header from "@/components/institutionalClient/Header";

// Define interface for icon props
interface IconProps {
  path: string;
  bgColor: string;
}

// Define interface for stat object
interface Stat {
  title: string;
  value: string;
  change: string;
  trend: "up" | "down" | "neutral";
  icon: IconProps;
}

const ViewData = () => {
  const { user, isAuthenticated, isLoading } = useAuthHook();
  const [clientProperty, setClientProperties] = useState<Property[]>([]);
  const [isLoadingProperties, setIsLoadingProperties] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { theme, setTheme } = useTheme();
  const { logout, token: authToken } = useAuth();

  function useTheme() {
    const [theme, setThemeState] = useState<"light" | "dark">("light");
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
      const storedTheme =
        (localStorage.getItem("theme") as "light" | "dark") || "light";
      setThemeState(storedTheme);
      setMounted(true);
    }, []);

    const setTheme = useCallback((newTheme: "light" | "dark") => {
      setThemeState(newTheme);
      localStorage.setItem("theme", newTheme);
    }, []);

    useEffect(() => {
      if (mounted) {
        document.documentElement.classList.toggle("dark", theme === "dark");
      }
    }, [theme, mounted]);

    return { theme, setTheme, mounted };
  }

  const apiUrl =
    import.meta.env.VITE_API_BASE_URL ||
    "https://valuate-api.vercel.app/api/v1";

  // Fetch client properties from API
  useEffect(() => {
    const fetchClientProperties = async () => {
      if (!user?.id) return;

      try {
        setIsLoadingProperties(true);
        setError(null);

        const token = localStorage.getItem("valuation_token");
        const response = await fetch(
          `${apiUrl}/properties/institutional/my-properties`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch properties: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.data) {
          setClientProperties(data.data);
        } else {
          setClientProperties([]);
        }
      } catch (err) {
        console.error("Error fetching properties:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load properties"
        );
      } finally {
        setIsLoadingProperties(false);
      }
    };

    if (isAuthenticated && user?.id) {
      fetchClientProperties();
    }
  }, [user?.id, isAuthenticated, apiUrl]);
  // Calculate stats based on fetched properties
  let clientTotalValue = 0;

  const clientProperties = [clientProperty].flat();
  const totalValue = clientProperties.reduce(
    (sum, property) => sum + (property?.totalValue || 0),
    0
  );
  const averageValue =
    clientProperties.length > 0 ? totalValue / clientProperties.length : 0;
  const taxEstimate = totalValue * 0.009; // Example: 0.9% property tax

  const navigate = useNavigate();
  const onPropertySelect = (propertyId: string) => {
    navigate(`/institutional-client-dashboard/view-data/property/${propertyId}`);
  };
  // Stats for client
  const stats: Stat[] = [
    {
      title: "Mu Properties",
      value: clientProperties.length.toString(),
      change: "otal properties",
      trend: "neutral",
      icon: {
        path: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
        bgColor: "bg-blue-500",
      },
    },
    {
      title: "Total Value",
      value: `$${totalValue.toLocaleString()}`,
      change: "Combined value",
      trend: "up",
      icon: {
        path: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
        bgColor: "bg-green-500",
      },
    },
    {
      title: "Average Value",
      value: `$${Math.round(averageValue).toLocaleString()}`,
      change: "Per property",
      trend: "neutral",
      icon: {
        path: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
        bgColor: "bg-yellow-500",
      },
    },
    {
      title: "Tax Estimate",
      value: `$${Math.round(taxEstimate).toLocaleString()}`,
      change: "Annual property tax",
      trend: "neutral",
      icon: {
        path: "M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z",
        bgColor: "bg-purple-500",
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const handleAction = (action: string) => {
    switch (action) {
      case "view-report":
        alert("Viewing report...");
        break;
      case "view-data":
        alert("Navigating to Data...");
        break;
      default:
        break;
    }
  };

  return (
    <div className="space-y-6">
       <Header theme={theme} setTheme={setTheme} logout={logout} /> 
      <div className="flex">
        <Sidebar
          onViewData={() => handleAction("view-data")}
          onViewReport={() => handleAction("view-report")}
        />
        <div className="flex-1 lg:ml-64 p-6 bg-gray-100 min-h-screen">
        
          {/* Welcome Message */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-lg font-medium text-indigo-600">
                  {user?.name?.charAt(0) || "U"}
                </span>
              </div>
              <div className="ml-4">
                <h2 className="text-lg font-medium text-gray-900">
                  Welcome back {user?.firstName || "User"}!
                </h2>
                <p className="text-gray-500">
                  Here's the latest information about your property portfolio.
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}


          {/* Properties */}
          <div className="mt-6">
            <h2 className="text-lg font-medium text-gray-800 mb-4">
              My Properties
            </h2>

            {isLoadingProperties ? (
              <div className="bg-white p-6 rounded-lg shadow-sm text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading properties...</p>
              </div>
            ) : clientProperties.length === 0 ? (
              <div className="bg-white p-6 rounded-lg shadow-sm text-center">
                <p className="text-gray-500">
                  You don't have any registered properties yet.
                </p>
                <button
                  onClick={() => (window.location.href = "/property/submit")}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Submit New Property
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6">
                {clientProperties.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    onClick={() => onPropertySelect(property.id)}
                    userRole={user?.role}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Information Cards */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Property Tax Information
              </h3>
              <div className="space-y-4 text-sm text-gray-600">
                <p>
                  Property taxes are calculated based on the assessed value of
                  your property and the local tax rate. The tax assessment is
                  usually conducted every 1-3 years.
                </p>
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="text-base font-medium text-gray-700 mb-2">
                    Payment Schedule
                  </h4>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>First installment due: March 15, 2024</li>
                    <li>Second installment due: September 15, 2024</li>
                    <li>Late payment penalty: 10% after due date</li>
                  </ul>
                </div>
                <div className="flex items-center pt-2 text-indigo-600">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <a href="#" className="font-medium hover:underline">
                    Learn more about property taxes
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Property Value Insights
              </h3>
              <div className="space-y-4 text-sm text-gray-600">
                <p>
                  Your property value is determined by various factors including
                  location, size, condition, and market trends.
                </p>
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="text-base font-medium text-gray-700 mb-2">
                    Value Improvement Tips
                  </h4>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Regular maintenance and repairs</li>
                    <li>Landscaping and curb appeal improvements</li>
                    <li>Energy-efficient upgrades</li>
                    <li>Interior renovations (kitchen, bathrooms)</li>
                  </ul>
                </div>
                <div className="flex items-center pt-2 text-indigo-600">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  <a href="#" className="font-medium hover:underline">
                    Request a new property assessment
                  </a>
                </div>
              </div>
            </div>
          </div> */}

          {/* Support */}
          <div className="bg-indigo-50 p-6 rounded-lg shadow-sm mt-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-indigo-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-indigo-900">
                  Need help?
                </h3>
                <p className="text-indigo-700">
                  Our property valuation experts are available to assist you
                  with any questions.
                </p>
                <div className="mt-3">
                  <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    Contact Support
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewData;
